import type { CityConfig } from "@/pages/city-template/types";

export const prescottConfig: CityConfig = {
  city: "Prescott",
  state: "Arizona",
  stateAbbr: "AZ",
  slug: "prescott",
  route: "/cities/prescott",
  title: "Emergency & Expedited Passport Services Prescott AZ | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Prescott, AZ — as fast as 24 hours. No Prescott passport agency. Fast Passport Center couriers to the Western Passport Center in Tucson, about 215 miles south. A+ BBB rated. Serving Yavapai County, Embry-Riddle students, and mile-high city travelers.",
  h1: "Emergency & Expedited Passport Services in Prescott, AZ",
  heroSubtitle: "Prescott sits in Arizona's mile-high central highlands, about 215 miles from the Western Passport Center in Tucson. Fast Passport Center's registered couriers deliver your documents to Tucson so you get a new or renewed passport as fast as 24 hours — whether you're an Embry-Riddle student heading overseas, a Prescott National Forest adventurer planning international travel, or a Yavapai County family with cruise plans.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Prescott%20Arizona%20historic%20downtown%20Whiskey%20Row%20at%20golden%20hour%20with%20Victorian%20architecture%20and%20the%20iconic%20courthouse%20plaza%20under%20dramatic%20sunset%20skies%2C%20pine-covered%20Bradshaw%20Mountains%20in%20the%20background%2C%20warm%20amber%20lighting%20on%20historic%20buildings%2C%20professional%20landscape%20photography%2C%20wide%20angle%20composition%2C%20no%20text%20or%20people%20visible&width=1800&height=700&seq=prescott-hero-001&orientation=landscape",
  heroImageAlt: "Prescott Arizona historic downtown and courthouse plaza at golden hour with Bradshaw Mountains",
  geoPosition: "34.5400;-112.4685",
  geoPlacename: "Prescott, Arizona",
  lastModified: "2026-06-05",
  regionalAgency: {
    name: "Western Passport Center",
    address: "733 N Stone Ave, Tucson, AZ 85701",
    distance: "Approximately 215 miles from Prescott — about 3.5 hours via I-17 and I-10",
  },
  nearbyCitiesZips: [
    { name: "Downtown Prescott", zips: "86301, 86303" },
    { name: "Prescott Valley", zips: "86314, 86315" },
    { name: "Dewey-Humboldt", zips: "86327" },
    { name: "Chino Valley", zips: "86323" },
    { name: "Cottonwood", zips: "86326" },
    { name: "Camp Verde", zips: "86322" },
    { name: "Sedona", zips: "86336" },
    { name: "Mayer", zips: "86333" },
    { name: "Cordes Lakes", zips: "86333" },
    { name: "Bagdad", zips: "86321" },
    { name: "Paulden", zips: "86334" },
    { name: "Skull Valley", zips: "86338" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Western Passport Center in Tucson — 215 miles from Prescott" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Central AZ Hub", desc: "Serving Prescott, Prescott Valley, Chino Valley, and all Yavapai County" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Prescott case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Flagstaff AZ", route: "/cities/flagstaff" },
    { name: "Expedited passport in Phoenix AZ", route: "/cities/phoenix" },
    { name: "Urgent passport in Scottsdale AZ", route: "/cities/scottsdale" },
    { name: "Arizona passport hub", route: "/states/arizona" },
    { name: "Expedited passport in Cottonwood AZ", route: "/cities/cottonwood" },
    { name: "Emergency passport in Prescott Valley AZ", route: "/cities/prescott-valley" },
    { name: "Fast passport in Sedona AZ", route: "/cities/sedona" },
  ],
  fpcOfficeAddress: "4742 N 24th St, Phoenix, AZ 85016",
  fpcOfficeNote: "Fast Passport Center serves Prescott remotely via secure courier. Our registered couriers hand-deliver your documents to the Western Passport Center in Tucson. Prescott sits along the I-17 corridor, making overnight document shipping fast and reliable from Yavapai County.",
  fpcOfficeDistance: "Serving all of Yavapai County",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Prescott, Prescott Valley, Chino Valley, and the Central Arizona highlands. From Embry-Riddle Aeronautical University students to Prescott National Forest adventurers, we help Yavapai County travelers get their passports fast.",
  nearbyCitiesIntro: "We serve Prescott and nearby communities including Prescott Valley, Chino Valley, Dewey-Humboldt, Cottonwood, Camp Verde, and the entire Quad Cities area.",
  howItWorksIntro: "Most Prescott travelers complete everything online and ship documents overnight. At about 215 miles from Tucson, Fast Passport Center provides fast courier turnaround for Central Arizona residents — ideal for Embry-Riddle students and Quad Cities families planning international trips.",
  mistakesIntro: "Prescott travelers, including Embry-Riddle students heading overseas and retirees planning international vacations, face unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "Prescott and Central Arizona travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Prescott? Here are the most common ones we hear from Yavapai County travelers.",
  statePageRoute: "/states/arizona",
  statePageName: "Arizona",
  dmvWebsite: "https://azdot.gov/motor-vehicles",
  dmvLabel: "Arizona MVD — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No drive or appointment needed",
        "Ideal for Embry-Riddle students studying abroad",
        "Perfect for Prescott retirees traveling internationally",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from PRC or PHX",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "About 215 miles to Tucson (3.5-hour drive)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Prescott is about 215 miles from the Western Passport Center in Tucson. Our couriers make same-day delivery possible, as fast as 24 hours.",
      bullets: ["Central AZ coverage", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in AZ",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Prescott adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Prescott and Yavapai County"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Prescott and Yavapai County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Prescott, Prescott Valley, and Quad Cities residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Tucson agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Yavapai County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Prescott and Central Arizona residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Prescott AZ", description: "Urgent passport processing for Prescott, AZ travelers via Western Passport Center courier — about 215 miles from Prescott." },
    { name: "Passport Renewal Prescott AZ", description: "Fast passport renewal for Prescott and Yavapai County, Arizona residents." },
    { name: "New Passport Prescott AZ", description: "First-time passport application assistance for Prescott and Quad Cities area residents." },
    { name: "Lost Passport Prescott AZ", description: "Passport replacement for lost or stolen passports for Prescott and Central Arizona residents." },
    { name: "Child Passport Prescott AZ", description: "Minor passport services for Prescott and Yavapai County families." },
    { name: "Name Change Passport Prescott AZ", description: "Passport name change service for Prescott, AZ residents." },
  ],
  testimonials: [
    {
      name: "Carter W.",
      location: "Embry-Riddle Student",
      rating: 5,
      text: "I'm an aeronautical science major doing a semester abroad in Singapore. Realized my passport was expired 6 days before my flight. Fast Passport Center handled everything — I had my new passport in 48 hours. Huge relief.",
      service: "Emergency Passport",
    },
    {
      name: "Janet M.",
      location: "Prescott Valley, AZ",
      rating: 5,
      text: "Retired couple planning a European river cruise. Our passports were set to expire 4 months after departure — didn't realize most countries require 6 months validity. Fast Passport Center got us renewed passports in 5 days. We didn't miss our cruise.",
      service: "Expedited Renewal",
    },
    {
      name: "Mike R.",
      location: "Downtown Prescott",
      rating: 5,
      text: "Business trip to London came up with 8 days notice. No valid passport. Fast Passport Center walked me through the DS-82 renewal and expedited everything to Tucson. Received in 4 business days. Made my meetings.",
      service: "Expedited Renewal",
    },
    {
      name: "Susan T.",
      location: "Chino Valley, AZ",
      rating: 5,
      text: "Three kids needing first passports for a family trip to Cancun. The acceptance clerk appointment was easy in Prescott, then Fast Passport Center rushed everything to Tucson. All four passports back in 7 days.",
      service: "Family Passports",
    },
  ],
  facilities: [
    {
      id: "prc-1",
      name: "Prescott Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "442 W Goodwin St",
      city: "Prescott",
      state: "AZ",
      zip: "86301",
      phone: "(928) 778-1890",
      hours: "Mon–Fri: 9:00 AM – 4:30 PM",
      distance: "Downtown Prescott",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for downtown Prescott. Appointment required. Call ahead for passport service hours.",
      icon: "ri-mail-line",
    },
    {
      id: "prc-2",
      name: "Yavapai County Clerk of Superior Court",
      type: "County Clerk Passport Acceptance",
      address: "120 S Cortez St, Room 107",
      city: "Prescott",
      state: "AZ",
      zip: "86301",
      phone: "(928) 771-3312",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "Downtown Prescott Courthouse Plaza",
      website: "https://courts.yavapaiaz.gov/clerk",
      websiteLabel: "Yavapai County Clerk",
      notes: "Accepts passport applications for Yavapai County residents. Convenient downtown courthouse plaza location.",
      icon: "ri-government-line",
    },
    {
      id: "prc-3",
      name: "Prescott Valley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "8300 E Spouse Dr",
      city: "Prescott Valley",
      state: "AZ",
      zip: "86314",
      phone: "(928) 775-6400",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8 miles east of Prescott",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient alternative for Prescott Valley and eastern Yavapai County residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "prc-4",
      name: "Chino Valley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "460 S Highway 89",
      city: "Chino Valley",
      state: "AZ",
      zip: "86323",
      phone: "(928) 636-2306",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "16 miles north of Prescott",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Chino Valley and northern Yavapai County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "prc-5",
      name: "Cottonwood Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "300 S Willard St",
      city: "Cottonwood",
      state: "AZ",
      zip: "86326",
      phone: "(928) 634-2281",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "28 miles east in Verde Valley",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Verde Valley and eastern Yavapai County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "prc-6",
      name: "Camp Verde Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "464 S 1st St",
      city: "Camp Verde",
      state: "AZ",
      zip: "86322",
      phone: "(928) 567-3081",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "38 miles southeast of Prescott",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Camp Verde and southeastern Yavapai County residents.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Tucson Agency", agency: "215 miles — 3.5-hr drive, but need appointment", fpc: "We drive for you — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Thinking a 3.5-Hour Drive to Tucson Is Worth It",
      description: "Even though Tucson is only 215 miles away via I-17 and I-10, the Western Passport Center has appointment-only access. Walk-ins are turned away. A wasted 7-hour round trip is not how you want to spend your day. We handle the appointment and delivery for you.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Specifications",
      description: "Passport photos must be exactly 2x2 inches with a white background. CVS and Walgreens in Prescott and Prescott Valley are generally reliable, but always verify your photos meet the exact requirements.",
    },
    {
      icon: "ri-file-list-line",
      title: "Using the Wrong Form — Embry-Riddle Student Mistakes",
      description: "Embry-Riddle students studying abroad often show up with DS-11 when they need DS-82, or vice versa. First-time passports use DS-11; renewals of adult passports use DS-82.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "Not Having Proof of Travel Ready",
      description: "Emergency processing at the Western Passport Center requires confirmed travel documents. Have your flight itinerary or cruise booking ready before reaching out — especially important for retirees with international travel plans.",
    },
    {
      icon: "ri-parent-line",
      title: "Both Parents Required for Minors",
      description: "Minors under 16 require both parents to appear, or one with notarized Form DS-3053. Prescott and Quad Cities families with one parent working out of town often miss this requirement.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "Despite living in Central Arizona, Prescott residents using standard USPS processing still wait 8-11 weeks. Embry-Riddle students should start passport applications at least 8 weeks before any study abroad departure.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Prescott, Prescott Valley, or Chino Valley." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Western Passport Center in Tucson — about 215 miles away." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Western Passport Center via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Prescott or Yavapai County address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Prescott from a passport agency?",
      a: "Prescott is approximately 215 miles from the Western Passport Center in Tucson — about a 3.5-hour drive via I-17 and I-10. However, the Tucson agency requires pre-scheduled appointments and does not accept walk-ins. Fast Passport Center handles the courier delivery so you don't need to make the trip.",
    },
    {
      q: "Can Embry-Riddle students get fast passports for study abroad?",
      a: "Yes. Embry-Riddle Aeronautical University students studying abroad are some of our most frequent customers in the Prescott area. We can process student passports in 2–9 business days with standard expedited service, or 24–48 hours for emergency processing. Start early — ideally 8 weeks before departure.",
    },
    {
      q: "How fast can I get an emergency passport in Prescott?",
      a: "Emergency processing is typically 24–48 hours for Prescott residents. Our couriers deliver directly to the Western Passport Center in Tucson, about 215 miles away. Standard expedited processing runs 2–9 business days.",
    },
    {
      q: "Does Prescott have its own passport agency?",
      a: "No. The closest regional passport agency is the Western Passport Center in Tucson, about 215 miles from Prescott. Prescott does have acceptance facilities at the Main Post Office and Yavapai County Clerk for first-time applications requiring in-person submission.",
    },
    {
      q: "What if I need a passport for Prescott National Forest international adventure travel?",
      a: "Prescott's outdoor recreation community — hikers, mountain bikers, and climbers — often plans international adventures in Patagonia, the Alps, or New Zealand. Fast Passport Center serves the outdoor community with expedited passport services so you're ready when adventure calls.",
    },
    {
      q: "Where do Prescott travelers fly internationally?",
      a: "Prescott travelers typically fly internationally through Phoenix Sky Harbor International Airport (PHX, 100 miles south), or Prescott Regional Airport (PRC) for connections to LAX or DEN for international flights. A valid U.S. passport is required for all international flights.",
    },
    {
      q: "How do I renew my passport in Prescott?",
      a: "Adult renewals (passport issued at 16 or older, within the past 15 years, in your current name) can be done entirely by mail with Form DS-82. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than standard USPS processing (8–11 weeks).",
    },
    {
      q: "Does the Prescott post office do passport photos?",
      a: "The Prescott Main Post Office on W Goodwin St accepts passport applications but may not offer photo services. CVS Pharmacy, Walgreens, and FedEx Office in Prescott offer passport photo services. Always verify your photos meet the exact 2x2 inch specification before submitting your application.",
    },
    {
      q: "What documents do I need for a first-time passport in Prescott?",
      a: "You'll need Form DS-11, a certified birth certificate, a valid photo ID, two passport photos, and fee payment. First-time applicants must appear in person at an acceptance facility such as the Prescott Main Post Office or Yavapai County Clerk.",
    },
    {
      q: "Can you serve Prescott Valley and Chino Valley residents too?",
      a: "Yes. We serve all of Yavapai County including Prescott Valley, Chino Valley, Dewey-Humboldt, and the entire Quad Cities area. Documents ship overnight from any UPS location in the Prescott area, and we deliver your passport back to your address.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Prescott", address: "530 Miller Valley Rd, Prescott, AZ 86301", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walgreens — Prescott", address: "1000 Willow Creek Rd, Prescott, AZ 86301", hours: "Mon–Sun: 7AM–10PM" },
    { name: "FedEx Office — Prescott", address: "1781 E State Rte 69, Prescott, AZ 86301", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–5PM" },
    { name: "Walgreens — Prescott Valley", address: "7550 E State Rte 69, Prescott Valley, AZ 86314", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Prescott Valley", address: "3040 N Windsong Dr, Prescott Valley, AZ 86314", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Prescott", address: "1781 E State Rte 69, Ste 42, Prescott, AZ 86301", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–4PM" },
  ],
  dmvLocations: [
    { name: "AZ MVD — Prescott", address: "1105 Commerce Dr, Prescott, AZ 86305", hours: "Mon–Fri: 8:00AM–5:00PM", note: "REAL ID and driver's license renewals for Yavapai County residents.", featured: true },
    { name: "AZ MVD — Cottonwood", address: "525 S 12th St, Cottonwood, AZ 86326", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves Verde Valley and eastern Yavapai County.", featured: false },
    { name: "AZ MVD — Flagstaff", address: "1959 S Woodlands Village Blvd, Flagstaff, AZ 86001", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves northern Arizona including portions of Yavapai County.", featured: false },
  ],
  airports: [
    {
      code: "PRC",
      name: "Prescott Regional Airport",
      distance: "8 miles north of downtown Prescott",
      address: "6500 MacCurdy Dr, Prescott, AZ 86301",
      note: "Prescott's local airport with connections to Denver and Los Angeles via United Express. About 15 minutes north via AZ-89. Passport required for international connections through DEN or LAX.",
      flightUrl: "https://www.prcairport.com/",
      flightLabel: "PRC Airport",
      featured: true,
    },
    {
      code: "PHX",
      name: "Phoenix Sky Harbor International Airport",
      distance: "100 miles south of Prescott",
      address: "3400 E Sky Harbor Blvd, Phoenix, AZ 85034",
      note: "Arizona's largest international airport with extensive domestic and global routes. About 1.5 hours south via I-17.",
      flightUrl: "https://www.skyharbor.com/",
      flightLabel: "PHX Airport",
      featured: false,
    },
    {
      code: "TUS",
      name: "Tucson International Airport",
      distance: "215 miles south of Prescott",
      address: "7250 S Tucson Blvd, Tucson, AZ 85756",
      note: "Southern Arizona's international hub. About 3.5 hours south via I-17 and I-10. Co-located with the Western Passport Center in Tucson.",
      flightUrl: "https://www.flytucson.com/",
      flightLabel: "TUS Airport",
      featured: false,
    },
  ],
};